import React, { useState } from "react";
import { getUserInfo } from "@/user";
import { MAX_EXPORT_TRIALS_WITH_TASKS, exportExcelReport, exportLimit } from "@/utils/excelReport/exportExcelReport";

const formatCount = (n) => n.toLocaleString("en-US");

const PROGRESS = {
  fetching: "Fetching trials",
  "fetching-layout": "Fetching the layout's data",
};

/**
 * "Export to Excel" (Show Report) for the selected trials: the customer's weekly file - All data, the layout's sheet
 * and Colors. Shows how far it is while it fetches the trials page by page; on an error the message with "Retry"
 * (nothing is downloaded then; only a 401 signs the user out, as everywhere). A layout with task fields takes at most
 * MAX_EXPORT_TRIALS_WITH_TASKS trials (a row per task).
 */
export default function ExcelReportButton({ trialIds = [], layout = null }) {
  const [progress, setProgress] = useState(null); // { phase, done, total } while exporting
  const [status, setStatus] = useState(null); // { type: 'ok' | 'error', text }

  const limit = exportLimit(layout);
  const tooMany = trialIds.length > limit;
  const running = progress !== null;

  const run = async () => {
    setStatus(null);
    setProgress({ phase: "fetching", done: 0, total: trialIds.length });
    try {
      const user = await getUserInfo();
      const { filename } = await exportExcelReport(trialIds, user, layout, setProgress);
      setStatus({ type: "ok", text: `Downloaded ${filename}.` });
    } catch (err) {
      console.error("Excel export failed", err);
      setStatus({ type: "error", text: "Could not create the Excel file: the trials could not all be fetched. Nothing was downloaded." });
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="excel-export">
      <button
        type="button"
        onClick={run}
        disabled={!layout || !trialIds.length || tooMany || running}
        className="min-h-10 rounded-md bg-green-600 px-4 py-2 font-medium text-white transition-colors duration-200 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {running ? "Exporting..." : "Export to Excel"}
      </button>
      {running && (
        <span role="status" className="text-sm text-gray-700 dark:text-gray-300" data-testid="excel-export-progress">
          {PROGRESS[progress.phase]
            ? `${PROGRESS[progress.phase]}: ${formatCount(progress.done)} of ${formatCount(progress.total)}...`
            : `Creating the file for ${formatCount(progress.total)} trials...`}
        </span>
      )}
      {!running && status?.type === "ok" && (
        <span role="status" className="text-sm text-emerald-700 dark:text-emerald-300">{status.text}</span>
      )}
      {!running && status?.type === "error" && (
        <span role="alert" className="flex flex-wrap items-center gap-2 text-sm text-red-700 dark:text-red-300">
          {status.text}
          <button type="button" onClick={run} className="min-h-10 rounded border border-red-300 px-3 font-semibold hover:bg-red-50 dark:border-red-700 dark:hover:bg-red-900/40">
            Retry
          </button>
        </span>
      )}
      {tooMany && (
        <span role="alert" className="text-sm text-amber-800 dark:text-amber-200" data-testid="excel-export-limit">
          {limit === MAX_EXPORT_TRIALS_WITH_TASKS
            ? `This layout has task fields, so the Excel file gets a row per task: at most ${formatCount(limit)} trials per export. ${formatCount(trialIds.length)} are selected - select fewer, or use a layout without task fields.`
            : `At most ${formatCount(limit)} trials per Excel export.`}
        </span>
      )}
    </div>
  );
}
